import { BytesPerMegaByte, UploadMaxFileSizes } from '@tempo-io/fim3-dto';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const DEFAULT_ACCEPTED_FILE_TYPES = ['.svg', '.png', '.jpg', '.jpeg', '.gif'];

export const formatAcceptedFileTypes = (acceptedFileTypes: string[]) =>
  acceptedFileTypes
    .map((extension) => extension.replace('.', '').toUpperCase())
    .join(', ')
    .replace(/, ([^,]*)$/, ' or $1');

/**
 * The extension and size rules for an attachment, as a translated message or null when the file is
 * acceptable.
 *
 * Shared rather than inlined because there are now two places a file is chosen - the attachments
 * component, which uploads immediately, and the add-evidence dialog, which holds the file until submit.
 * Two copies of this rule would drift, and the one that drifts is the one nobody is looking at.
 */
export const useAttachmentValidation = (
  acceptedFileTypes: string[] = DEFAULT_ACCEPTED_FILE_TYPES,
) => {
  const { t } = useTranslation('translation');

  return useCallback(
    (file: File): string | null => {
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

      if (!acceptedFileTypes.includes(fileExtension)) {
        return t(($) => $.ideaManagement.general.fileTypeNotAccepted, {
          acceptedFileTypes: formatAcceptedFileTypes(acceptedFileTypes),
        });
      }

      if (file.size > UploadMaxFileSizes.attachment) {
        return t(($) => $.ideaManagement.general.fileExceedsMaximumSize, {
          maxSize: UploadMaxFileSizes.attachment / BytesPerMegaByte,
        });
      }

      return null;
    },
    [acceptedFileTypes, t],
  );
};
